import { OiamEvents } from '@oiambk/oiam-oauth-types';
import { Component, Env } from '@stencil/core';
import log from 'loglevel';
import { EventActionDispatcher } from '../services/event-action-dispatcher';
import { EventQueue } from '../services/event-queue';
import { fallbackConfig } from '../services/fetch-default-config';
import { NginxErrorLogger } from '../services/nginx-error-logger';
import { OauthEventService } from '../services/oauth-event-service';
import { OauthService } from '../services/oauth-service';
import { StoreService } from '../services/store-service';

@Component({
  tag: 'oiam-oauth-fallback',
  shadow: true,
})
export class OiamOauthFallbackComponent {
  isOiamOauthComponetLoaded: boolean = false;
  private eventQueue: EventQueue;

  async componentWillLoad() {
    log.debug('oiam-oauth-fallback-wc: will-load');
    // wait time for hosted component from environment if set; default 2 seconds
    const waittime: number = Env.FALLBACK_WAIT_TIME ? Number.parseInt(Env.FALLBACK_WAIT_TIME) : 2000;
    // check oiam-oauth-wc exists
    window.self.addEventListener(
      OiamEvents.LOADED,
      () => {
        this.isOiamOauthComponetLoaded = true;
      },
      { once: true },
    );
    // just wait 2s
    await new Promise(resolve => {
      setTimeout(resolve, waittime);
    });
    // we might miss the loaded event
    // additional check for hydrated attribute
    if (!this.isOiamOauthComponetLoaded) {
      const oauthWcs = document.getElementsByTagName('oiam-oauth-component');
      if (oauthWcs.length > 0) {
        const attrClass = oauthWcs[0].getAttribute('class');
        if (attrClass && attrClass.includes('hydrated')) {
          this.isOiamOauthComponetLoaded = true;
        }
      }
    }

    // check hosted web component is loaded
    if (this.isOiamOauthComponetLoaded) {
      // return never resolving promise -> component will never load
      return new Promise(() => {});
    }

    // make sure only one instance is loaded
    if (!StoreService.createFallbackInstance()) {
      // return never resolving promise -> component will never load
      return new Promise(() => {});
    }

    const config = fallbackConfig();
    // getting path dynamically might not work on a npm loaded component
    // set with hard coded path
    NginxErrorLogger.setLogUrl('/oiambk/oiam-oauth-wc/ct/mt/clienterror/');
    this.eventQueue = new EventQueue(new EventActionDispatcher());
    await OauthService.init(config);
  }

  render() {
    return '';
  }

  componentDidLoad() {
    log.debug('oiam-oauth-wc did-render: register events');
    window.self.addEventListener(OiamEvents.DOLOGIN, e => this.eventQueue.push(e as CustomEvent));
    window.self.addEventListener(OiamEvents.SETCONFIG, e => this.eventQueue.push(e as CustomEvent));
    window.self.addEventListener(OiamEvents.DOLOGOUT, e => this.eventQueue.push(e as CustomEvent));
    window.self.addEventListener(OiamEvents.SENDTOKEN, e => this.eventQueue.push(e as CustomEvent));
    window.self.addEventListener(OiamEvents.SENDSESSION, e => this.eventQueue.push(e as CustomEvent));
    document.addEventListener('visibilitychange', () => this.doStateActionsIfVisible());

    // handle loading from back-forward-cache
    window.addEventListener('pageshow', async event => {
      if (event.persisted) {
        // validate the correct state
        await this.eventQueue.push(new CustomEvent('loadedFromCache'));
      }
    });

    // loaded
    OauthEventService.doDispatchEvent(OiamEvents.LOADED, true);
  }

  private async doStateActionsIfVisible() {
    // render component anew
    if (document.visibilityState === 'visible') {
      await this.eventQueue.push(new CustomEvent('oiamHasBecomeVisible'));
    }
  }
}
